import { TabContext } from "@mui/lab";
import { Box, Divider, useTheme } from "@mui/material";
import { t } from "i18next";
import { Common } from "../../../../common/src";
import {
  DETAIL_TAB,
  EViewerMode,
  ProjectDetailContext,
  UseSnackbarContext,
} from "../Interfaces/ProjectDetailInterfaces";
import Q3DIconButton from "../../Parts/Q3DIconButton";
import PhotoCamera from "../../../../images/icons/white_edit/PhotoCamera_white.svg";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";

import { DefaultTabPanel } from "./DefaultTabPanel";
import { CalcTabPanel } from "./CalcTabPanel";
import { HelmTabPanel } from "./HelmTabPanel";
import { RemoTabPanel } from "./RemoTabPanel";
import { ExpoTabPanel } from "./ExpoTabPanel";
import { useContext, useState, useEffect } from "react";
import { setViewType } from "../../../viewer/config/ViewerConfig";
import html2canvas from "html2canvas";
import { ShowToolMenu } from "./ShowToolMenu";
import {
  BoxBackIcon,
  BoxBottomIcon,
  BoxFrontIcon,
  BoxLeftIcon,
  BoxTopSameDirectionIcon,
  BoxRightIcon,
  BoxTopIcon,
  PhotoIcon,
} from "../../Parts/Q3DIcons";
import IOSSwitch from "../../IOSSwitch";
import { useViewerValue } from "../../../viewer/context";

const DARK = "dark";

type Props = {
  projectDetail: Common.ProjectDetail;
  currentViewerMode: EViewerMode;
};

export const ToolBar = (props: Props) => {
  const theme = useTheme();
  const { viewer, detailTab, projectDetail, isArView, isIosView } =
    useContext(ProjectDetailContext);
  const { openSuccess, openError } = useContext(UseSnackbarContext);

  const [isIfcCentered, setIsIfcCentered] = useState(false);
  const [isIfcDisplayed, setIsIfcDisplayed] = useState(false);

  const customBackgroundColor = {
    background: theme.palette.base.main,
  };
  const customColor = {
    color: theme.palette.base.contrast,
  };

  const btnSize = "36px";
  const toolBarLeftWidth = "calc(100% - 360px - 100ppx)"; // btnSize * 10

  const takeScreenshot = async () => {
    const photoShot: HTMLElement = document.querySelector("#point-view3");
    html2canvas(photoShot)
      .then((canvas) => {
        if (!isIosView) {
          canvas.toBlob((blob) =>
            navigator.clipboard.write([
              new ClipboardItem({ "image/png": blob }),
            ]),
          );
        } else {
          const blob = canvas.toDataURL("image/jpeg");
          (window as any).webkit.messageHandlers.callback.postMessage({
            type: "clipboard",
            props: { image: blob },
          });
        }
        openSuccess(t("COPY_SUCCESS"));
      })
      .catch(() => {
        openError(t("COPY_FAILED"));
      });
  };

  const setIfcDisplayState = (display: boolean) => {
    if (display) {
      setIsIfcDisplayed(true);
      if (isArView && !projectDetail.las_file) {
        setIfcDisplayPosition(true);
      }
    } else {
      setIsIfcDisplayed(false);
    }
  };

  const setIfcDisplayPosition = (display: boolean) => {
    if (display) {
      setIsIfcCentered(true);
      viewer.designOffsetMode = "bound";
    } else {
      setIsIfcCentered(false);
      viewer.designOffsetMode = "cloudpoint";
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        padding: "4px 16px",
        display: "flex",
        justifyContent: "space-between",
        ...customBackgroundColor,
      }}
      // container
      alignItems="center"
    >
      {/* <Box item xs={4}> */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          wordWrap: "break-word",
          minWidth: "0",
          marginRight:
            props.projectDetail?.status.ply_upload !==
            Common.PROJECT_STATUS_ENUM.COMPLETED
              ? "0px"
              : "48px",
        }}
      >
        <Q3DIconButton
          title={t("SCREENSHOT")}
          titleType="tooltip"
          onClick={() => {
            takeScreenshot();
          }}
          icon={<PhotoIcon />}
          height={btnSize}
          width={btnSize}
          disabled={detailTab === DETAIL_TAB.PHOTO}
        />
        <Divider
          orientation="vertical"
          sx={{ border: "4px solid #00000000" }}
        />
        <Q3DIconButton
          title={t("CENTER")}
          titleType="tooltip"
          onClick={() => {
            viewer.fit();
          }}
          icon={
            <GpsFixedIcon
              fontSize="small"
              style={{ width: "24px", height: "24px", ...customColor }}
            />
          }
          height={btnSize}
          width={btnSize}
          disabled={detailTab === DETAIL_TAB.PHOTO}
        />
        <Q3DIconButton
          title={t("FRONT")}
          titleType="tooltip"
          onClick={() => {
            viewer.setView(setViewType.FRONT);
          }}
          icon={<BoxFrontIcon width={24} height={24} />}
          height={btnSize}
          width={btnSize}
          disabled={detailTab === DETAIL_TAB.PHOTO}
        />
        <Q3DIconButton
          title={t("BACK")}
          titleType="tooltip"
          onClick={() => {
            viewer.setView(setViewType.BACK);
          }}
          icon={<BoxBackIcon width={24} height={24} />}
          height={btnSize}
          width={btnSize}
          disabled={detailTab === DETAIL_TAB.PHOTO}
        />
        <Q3DIconButton
          title={t("LEFT")}
          titleType="tooltip"
          onClick={() => {
            viewer.setView(setViewType.LEFT);
          }}
          icon={<BoxLeftIcon width={24} height={24} />}
          height={btnSize}
          width={btnSize}
          disabled={detailTab === DETAIL_TAB.PHOTO}
        />
        <Q3DIconButton
          title={t("RIGHT_SIDE")}
          titleType="tooltip"
          onClick={() => {
            viewer.setView(setViewType.RIGHT);
          }}
          icon={<BoxRightIcon width={24} height={24} />}
          height={btnSize}
          width={btnSize}
          disabled={detailTab === DETAIL_TAB.PHOTO}
        />
        <Q3DIconButton
          title={t("ABOVE")}
          titleType="tooltip"
          onClick={() => {
            viewer.setView(setViewType.TOP);
          }}
          icon={<BoxTopIcon width={24} height={24} />}
          height={btnSize}
          width={btnSize}
          disabled={detailTab === DETAIL_TAB.PHOTO}
        />
        <Q3DIconButton
          title={t("BOTTOM")}
          titleType="tooltip"
          onClick={() => {
            viewer.setView(setViewType.BOTTOM);
          }}
          icon={<BoxBottomIcon width={24} height={24} />}
          height={btnSize}
          width={btnSize}
          disabled={detailTab === DETAIL_TAB.PHOTO}
        />
        <Q3DIconButton
          title={t("TOP_SAME_DIRECTION")}
          titleType="tooltip"
          onClick={() => {
            viewer.setView(setViewType.TOP_SAME_DIRECTION);
          }}
          icon={<BoxTopSameDirectionIcon width={24} height={24} />}
          height={btnSize}
          width={btnSize}
          disabled={detailTab === DETAIL_TAB.PHOTO}
        />
        <Divider
          orientation="vertical"
          sx={{ border: "4px solid #00000000" }}
        />
        <ShowToolMenu
          projectDetail={props.projectDetail}
          onChangeIfcState={setIfcDisplayState}
        />
        <Divider
          orientation="vertical"
          sx={{ border: "4px solid #00000000" }}
        />
        {isIfcDisplayed && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              wordWrap: "break-word",
              minWidth: "0",
              marginRight:
                props.projectDetail?.status.ply_upload !==
                Common.PROJECT_STATUS_ENUM.COMPLETED
                  ? "0px"
                  : "48px",
            }}
          >
            {" "}
            <IOSSwitch
              checked={isIfcCentered}
              onChange={(e) => {
                setIfcDisplayPosition(e.target.checked);
              }}
              disabled={isArView && !projectDetail.las_file}
            />
            <Divider
              orientation="vertical"
              sx={{ border: "4px solid #00000000" }}
            />
            <div style={{ color: theme.palette.base.contrast }}>
              {isIfcCentered
                ? t("IFC_DISPLAY_CENTERED")
                : t("IFC_DISPLAY_GEOREFERENCED")}
            </div>
          </Box>
        )}
      </Box>
      {/* </Grid>
      <Grid item xs={8} sx={{}}> */}
      <TabContext value={props.currentViewerMode}>
        {!isIosView ? <DefaultTabPanel width={`${toolBarLeftWidth}`} /> : <></>}
        <CalcTabPanel width={`${toolBarLeftWidth}`} />
        <HelmTabPanel width={`${toolBarLeftWidth}`} />
        <RemoTabPanel width={`${toolBarLeftWidth}`} />
        <ExpoTabPanel width={`${toolBarLeftWidth}`} />
      </TabContext>
      {/* </Grid> */}
    </Box>
  );
};
